import React, { useState, useEffect, useRef } from "react";
import CustomerList from "./CustomerList";
import { useLocation } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";

const AdminCustomer = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [localSearchValue, setLocalSearchValue] = useState(""); // Local input value
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const debounceTimer = useRef(null);

  // Lấy các tham số từ URL khi component mount
  useEffect(() => {
    const search = params.get("search") || "";
    const sort = params.get("sortBy") || "";
    const order = params.get("sortOrder") || "asc";
    const page = parseInt(params.get("page")) || 1;

    setSearchKeyword(search);
    setLocalSearchValue(search); // Sync local value
    setSortBy(sort);
    setSortOrder(order);
    setCurrentPage(page);
  }, [location.search]);

  // Debounce search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (localSearchValue !== searchKeyword) {
        setSearchKeyword(localSearchValue);
        setCurrentPage(1);
        updateURL(localSearchValue, sortBy, sortOrder, 1);
      }
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearchValue, sortBy, sortOrder]);

  const handleSearchKeywordChange = (event) => {
    const value = event.target.value;
    setLocalSearchValue(value); // Only update local state immediately
  };

  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
    setCurrentPage(1); // Reset về trang 1
    updateURL(searchKeyword, event.target.value, sortOrder, 1);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
    setCurrentPage(1); // Reset về trang 1
    updateURL(searchKeyword, sortBy, event.target.value, 1);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    // Clear debounce timer and search immediately
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchKeyword(localSearchValue);
    setCurrentPage(1);
    updateURL(localSearchValue, sortBy, sortOrder, 1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateURL(searchKeyword, sortBy, sortOrder, newPage);
  };

  const updateURL = (search, sort, order, newPage = 1) => {
    const params = new URLSearchParams();
    params.set("page", newPage.toString());

    if (search.trim()) {
      params.set("search", search.trim());
    }
    if (sort) {
      params.set("sortBy", sort);
      params.set("sortOrder", order);
    }

    // Cập nhật URL mà không reload trang
    window.history.replaceState(
      {},
      "",
      `/admin/customers?${params.toString()}`
    );
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setLocalSearchValue(""); // Clear local value too
    setSortBy("");
    setSortOrder("asc");
    setCurrentPage(1);
    window.history.replaceState({}, "", "/admin/customers?page=1");
  };

  return (
    <div className="bg-gray-50">
      <div className="min-h-screen overflow-y-auto">
        <div className="p-1 space-y-2 overflow-y-auto h-screen">
          {/* Header + Filters Combined */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Quản lý khách hàng
                </h1>
                <p className="text-gray-600 mt-1">
                  Quản lý thông tin khách hàng trong hệ thống
                </p>
              </div>
            </div>

            {/* Filters Section */}
            <div className="border-t border-gray-200 pt-3">
              <form onSubmit={handleSearchSubmit} className="space-y-4">
                {/* All Filters in One Row */}
                <div className="flex flex-wrap items-end gap-4">
                  {/* Search Input */}
                  <div className="flex-1 min-w-64">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tìm kiếm khách hàng
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Nhập tên, email hoặc số điện thoại..."
                        value={localSearchValue}
                        onChange={handleSearchKeywordChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                      />

                      {localSearchValue !== searchKeyword && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div
                            className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"
                            title="Đang tìm kiếm..."
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sort By */}
                  <div className="flex-shrink-0">
                    <label className="block text-sm font-medium text-gray-700 ">
                      Sắp xếp theo
                    </label>
                    <select
                      value={sortBy}
                      onChange={handleSortByChange}
                      className="w-40 px-3 py-2 !m-0 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                    >
                      <option value="">Mặc định</option>
                      <option value="fullName">Họ tên</option>
                      <option value="email">Email</option>
                      <option value="phoneNumber">Số điện thoại</option>
                    </select>
                  </div>

                  {/* Sort Order */}
                  <div className="flex-shrink-0">
                    <label className="block text-sm font-medium text-gray-700 ">
                      Thứ tự
                    </label>
                    <select
                      value={sortOrder}
                      onChange={handleSortOrderChange}
                      disabled={!sortBy}
                      className="w-32 px-3 py-2 !m-0 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="asc">Tăng dần</option>
                      <option value="desc">Giảm dần</option>
                    </select>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-end gap-2">
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                    >
                      <SearchOutlined />
                      Tìm kiếm
                    </button>

                    <button
                      type="button"
                      onClick={clearFilters}
                      className="inline-flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                    >
                      Xóa bộ lọc
                    </button>
                  </div>
                </div>
              </form>

              {/* Active Filters Display */}
              {(searchKeyword || sortBy) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      Bộ lọc hiện tại:
                    </span>

                    {searchKeyword && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Tìm kiếm: "{searchKeyword}"
                      </span>
                    )}

                    {sortBy && (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Sắp xếp:{" "}
                        {sortBy === "fullName"
                          ? "Họ tên"
                          : sortBy === "email"
                          ? "Email"
                          : "Số điện thoại"}{" "}
                        ({sortOrder === "asc" ? "Tăng dần" : "Giảm dần"})
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <CustomerList
              searchKeyword={searchKeyword}
              sortBy={sortBy}
              sortOrder={sortOrder}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomer;
